import { nonNullable } from 'lib/array/arrayUtils'

export interface HotelBookingContextSection {
  kind: 'hotel'
  orderId: string
  bookingItemId: string
  offerId?: string
  property: string
  checkIn?: string
  checkOut?: string
}

export interface ExperienceBookingContextSection {
  kind: 'experience'
  orderId: string
  bookingItemId: string
  offerId: string
  experience?: string
  location?: string
  date?: string
  time?: string
}

export interface FlightBookingContextSection {
  kind: 'flight'
  orderId: string
  bookingItemId: string
  reservationId?: string
  flightNumbers?: string
  tripLegs?: string
  from?: string
  to?: string
  departureDate?: string
  arrivalDate?: string
}

export type BookingContextSection =
  | HotelBookingContextSection
  | ExperienceBookingContextSection
  | FlightBookingContextSection

export interface BookedFlightReference {
  kind: 'bookedFlight'
  orderId: string
  orderFlightItemId: string
}

// Persisted in chat history and parsed by svc-trip (contextOnlyExperienceInclusions.ts, requestedOrderFlights.ts); renaming breaks stored chats.
const SECTION_HEADINGS = {
  hotel: 'Hotel booking',
  experience: 'Experience booking',
  flight: 'Flight booking',
} as const

const FIELD_LABELS = {
  orderId: 'Order ID',
  bookingItemId: 'Booking item ID',
  offerId: 'Offer ID',
  property: 'Property',
  checkIn: 'Check-in',
  checkOut: 'Check-out',
  experience: 'Experience',
  location: 'Location',
  date: 'Date',
  time: 'Time',
  reservationId: 'Reservation ID',
  flightNumbers: 'Flight number',
  tripLegs: 'Trip legs',
  from: 'From',
  to: 'To',
  departureDate: 'Departure date',
  arrivalDate: 'Arrival date',
} as const

const BOOKED_FLIGHT_KEYS = {
  orderId: 'order_id',
  orderFlightItemId: 'order_flight_item_id',
} as const

const BOOKING_CONTEXT_PREAMBLE = 'Please add the following existing bookings to my trip:'

const CONTEXT_BLOCK_REGEX = /<Context(?:\s+label="[^"]*")?>([\s\S]*?)<\/Context>/gi

type FieldLabel = typeof FIELD_LABELS[keyof typeof FIELD_LABELS]

function serialiseSection(heading: string, fields: Array<[FieldLabel, string | undefined]>): string {
  const lines = fields.flatMap(([label, value]) => (value ? [`${label}: ${value}`] : []))
  return [heading, ...lines].join('\n')
}

function serialiseBookingContextSection(section: BookingContextSection): string {
  switch (section.kind) {
    case 'hotel':
      return serialiseSection(SECTION_HEADINGS.hotel, [
        [FIELD_LABELS.orderId, section.orderId],
        [FIELD_LABELS.bookingItemId, section.bookingItemId],
        [FIELD_LABELS.offerId, section.offerId],
        [FIELD_LABELS.property, section.property],
        [FIELD_LABELS.checkIn, section.checkIn],
        [FIELD_LABELS.checkOut, section.checkOut],
      ])
    case 'experience':
      return serialiseSection(SECTION_HEADINGS.experience, [
        [FIELD_LABELS.orderId, section.orderId],
        [FIELD_LABELS.bookingItemId, section.bookingItemId],
        [FIELD_LABELS.offerId, section.offerId],
        [FIELD_LABELS.experience, section.experience],
        [FIELD_LABELS.location, section.location],
        [FIELD_LABELS.date, section.date],
        [FIELD_LABELS.time, section.time],
      ])
    case 'flight':
      return serialiseSection(SECTION_HEADINGS.flight, [
        [FIELD_LABELS.orderId, section.orderId],
        [FIELD_LABELS.bookingItemId, section.bookingItemId],
        [FIELD_LABELS.reservationId, section.reservationId],
        [FIELD_LABELS.flightNumbers, section.flightNumbers],
        [FIELD_LABELS.tripLegs, section.tripLegs],
        [FIELD_LABELS.from, section.from],
        [FIELD_LABELS.to, section.to],
        [FIELD_LABELS.departureDate, section.departureDate],
        [FIELD_LABELS.arrivalDate, section.arrivalDate],
      ])
  }
}

/** The body of the hidden context block that asks the agent to add existing bookings to the trip. */
export function serialiseBookingContext(sections: Array<BookingContextSection>): string {
  return [BOOKING_CONTEXT_PREAMBLE, ...sections.map(serialiseBookingContextSection)].join('\n\n')
}

/** Context blocks that link booked flights by id alone, for flight-only selections. */
export function serialiseBookedFlightContext(references: Array<BookedFlightReference>): string {
  return references
    .map(reference => `<Context>\n${BOOKED_FLIGHT_KEYS.orderId}: ${reference.orderId}\n${BOOKED_FLIGHT_KEYS.orderFlightItemId}: ${reference.orderFlightItemId}\n</Context>`)
    .join('\n')
}

function splitLines(text: string): Array<string> {
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

function parseKeyValueLines(lines: Array<string>): Map<string, string> {
  return new Map(lines.flatMap((line): Array<[string, string]> => {
    const separatorIndex = line.indexOf(':')
    if (separatorIndex < 0) {
      return []
    }

    const key = line.slice(0, separatorIndex).trim().toLowerCase()
    const value = line.slice(separatorIndex + 1).trim()
    return key && value ? [[key, value]] : []
  }))
}

function parseBookingContextSection(section: string): BookingContextSection | undefined {
  const [heading, ...fieldLines] = splitLines(section)
  const fields = parseKeyValueLines(fieldLines)
  const getField = (label: FieldLabel) => fields.get(label.toLowerCase())
  const orderId = getField(FIELD_LABELS.orderId)
  const bookingItemId = getField(FIELD_LABELS.bookingItemId)

  if (!orderId || !bookingItemId) {
    return undefined
  }

  switch (heading) {
    case SECTION_HEADINGS.hotel: {
      const property = getField(FIELD_LABELS.property)
      if (!property) {
        return undefined
      }

      return {
        kind: 'hotel',
        orderId,
        bookingItemId,
        offerId: getField(FIELD_LABELS.offerId),
        property,
        checkIn: getField(FIELD_LABELS.checkIn),
        checkOut: getField(FIELD_LABELS.checkOut),
      }
    }
    case SECTION_HEADINGS.experience: {
      const offerId = getField(FIELD_LABELS.offerId)
      if (!offerId) {
        return undefined
      }

      return {
        kind: 'experience',
        orderId,
        bookingItemId,
        offerId,
        experience: getField(FIELD_LABELS.experience),
        location: getField(FIELD_LABELS.location),
        date: getField(FIELD_LABELS.date),
        time: getField(FIELD_LABELS.time),
      }
    }
    case SECTION_HEADINGS.flight:
      return {
        kind: 'flight',
        orderId,
        bookingItemId,
        reservationId: getField(FIELD_LABELS.reservationId),
        flightNumbers: getField(FIELD_LABELS.flightNumbers),
        tripLegs: getField(FIELD_LABELS.tripLegs),
        from: getField(FIELD_LABELS.from),
        to: getField(FIELD_LABELS.to),
        departureDate: getField(FIELD_LABELS.departureDate),
        arrivalDate: getField(FIELD_LABELS.arrivalDate),
      }
    default:
      return undefined
  }
}

function parseBookedFlightReference(contextBlock: string): BookedFlightReference | undefined {
  const fields = parseKeyValueLines(splitLines(contextBlock))
  const orderId = fields.get(BOOKED_FLIGHT_KEYS.orderId)
  const orderFlightItemId = fields.get(BOOKED_FLIGHT_KEYS.orderFlightItemId)

  return orderId && orderFlightItemId ? { kind: 'bookedFlight', orderId, orderFlightItemId } : undefined
}

/** Every booking a message's context blocks describe, in message order. */
export function parseBookingContext(message: string): Array<BookingContextSection | BookedFlightReference> {
  return [...message.matchAll(CONTEXT_BLOCK_REGEX)].flatMap((contextBlock) => {
    const blockContent = contextBlock[1]?.trim()
    if (!blockContent) {
      return []
    }

    const sections = blockContent
      .split(/\n\s*\n+/)
      .map(section => parseBookingContextSection(section))
    return nonNullable([parseBookedFlightReference(blockContent), ...sections])
  })
}
